import { Loader2, Mail, Bell, MessageSquare, TrendingUp, Megaphone, Calendar } from "lucide-react";

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { useEmailPreferences } from "@/hooks/useEmailPreferences";

export const EmailPreferences = () => {
  const { preferences, loading, saving, updatePreferences } = useEmailPreferences();

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Mail className="h-5 w-5" />
          Email Preferences
        </CardTitle>
        <CardDescription>
          Control which emails you receive and how often
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Email Types */}
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-foreground">Email Types</h3>
          
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-start gap-3 flex-1">
              <Bell className="h-5 w-5 text-muted-foreground mt-0.5" />
              <div className="space-y-0.5">
                <Label htmlFor="onboarding_emails" className="text-sm font-medium cursor-pointer">
                  Onboarding Emails
                </Label>
                <p className="text-xs text-muted-foreground">
                  Tips and guidance to help you get started
                </p>
              </div>
            </div>
            <Switch
              id="onboarding_emails"
              checked={preferences.onboarding_emails}
              onCheckedChange={(checked) => updatePreferences({ onboarding_emails: checked })}
              disabled={saving}
            />
          </div>

          <div className="flex items-center justify-between gap-4">
            <div className="flex items-start gap-3 flex-1">
              <MessageSquare className="h-5 w-5 text-muted-foreground mt-0.5" />
              <div className="space-y-0.5">
                <Label htmlFor="waitlist_emails" className="text-sm font-medium cursor-pointer">
                  Waitlist Updates
                </Label>
                <p className="text-xs text-muted-foreground">
                  Status changes and approval notifications
                </p>
              </div>
            </div>
            <Switch
              id="waitlist_emails"
              checked={preferences.waitlist_emails}
              onCheckedChange={(checked) => updatePreferences({ waitlist_emails: checked })}
              disabled={saving}
            />
          </div>

          <div className="flex items-center justify-between gap-4">
            <div className="flex items-start gap-3 flex-1">
              <Bell className="h-5 w-5 text-muted-foreground mt-0.5" />
              <div className="space-y-0.5">
                <Label htmlFor="admin_emails" className="text-sm font-medium cursor-pointer">
                  Admin Notifications
                </Label>
                <p className="text-xs text-muted-foreground">
                  Important system updates and announcements
                </p>
              </div>
            </div>
            <Switch
              id="admin_emails"
              checked={preferences.admin_emails}
              onCheckedChange={(checked) => updatePreferences({ admin_emails: checked })}
              disabled={saving}
            />
          </div>

          <div className="flex items-center justify-between gap-4">
            <div className="flex items-start gap-3 flex-1">
              <Megaphone className="h-5 w-5 text-muted-foreground mt-0.5" />
              <div className="space-y-0.5">
                <Label htmlFor="marketing_emails" className="text-sm font-medium cursor-pointer">
                  Marketing & Updates
                </Label>
                <p className="text-xs text-muted-foreground">
                  Product news, features, and promotions
                </p>
              </div>
            </div>
            <Switch
              id="marketing_emails"
              checked={preferences.marketing_emails}
              onCheckedChange={(checked) => updatePreferences({ marketing_emails: checked })}
              disabled={saving}
            />
          </div>
        </div>

        <Separator />

        {/* Digest Settings */}
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-foreground">Activity Digests</h3>
          
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-start gap-3 flex-1">
              <TrendingUp className="h-5 w-5 text-muted-foreground mt-0.5" />
              <div className="space-y-0.5">
                <Label htmlFor="digest_emails" className="text-sm font-medium cursor-pointer">
                  Daily/Weekly Digests
                </Label>
                <p className="text-xs text-muted-foreground">
                  Summary of your activity and progress
                </p>
              </div>
            </div>
            <Switch
              id="digest_emails"
              checked={preferences.digest_emails}
              onCheckedChange={(checked) => updatePreferences({ digest_emails: checked })}
              disabled={saving}
            />
          </div>

          {preferences.digest_emails && (
            <div className="ml-8 space-y-2">
              <Label htmlFor="digest_frequency" className="text-sm">
                Digest Frequency
              </Label>
              <Select
                value={preferences.digest_frequency}
                onValueChange={(value) => updatePreferences({ digest_frequency: value as any })}
                disabled={saving}
              >
                <SelectTrigger id="digest_frequency" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="never">Never</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        <Separator />

        {/* Content Recommendations */}
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-foreground">Content Recommendations</h3>
          
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-start gap-3 flex-1">
              <Calendar className="h-5 w-5 text-muted-foreground mt-0.5" />
              <div className="space-y-0.5">
                <Label htmlFor="content_recommendations" className="text-sm font-medium cursor-pointer">
                  Personalized Content
                </Label>
                <p className="text-xs text-muted-foreground">
                  Suggested frameworks, templates, and resources
                </p>
              </div>
            </div>
            <Switch
              id="content_recommendations"
              checked={preferences.content_recommendations}
              onCheckedChange={(checked) => updatePreferences({ content_recommendations: checked })}
              disabled={saving}
            />
          </div>

          {preferences.content_recommendations && (
            <div className="ml-8 space-y-2">
              <Label htmlFor="recommendation_frequency" className="text-sm">
                Recommendation Frequency
              </Label>
              <Select
                value={preferences.recommendation_frequency}
                onValueChange={(value) => updatePreferences({ recommendation_frequency: value as any })}
                disabled={saving}
              >
                <SelectTrigger id="recommendation_frequency" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="biweekly">Every 2 Weeks</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="never">Never</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
